/*
 * gmx-cart.js — cart page behaviour for sections/gmx-cart.liquid.
 *
 * Flow for every quantity change or removal:
 *   1. Optimistic UI: the row dims, its controls lock, and the stepper number
 *      updates straight away. Money is NOT guessed client-side — Shopify owns
 *      formatting, discounts and totals.
 *   2. POST /cart/change.js with the line item key, the new quantity, and the
 *      Section Rendering API parameters, so one request returns both the new
 *      cart JSON and the freshly rendered section HTML.
 *   3. Swap the inner HTML of [data-gmx-cart] with the returned markup and set
 *      the header badge from response.item_count. Listeners are delegated on
 *      the root, so they survive the swap.
 *
 * The promo field never does discount math either. It uses Shopify's own
 * mechanism, /discount/<code>, but in the background: the request is redirected
 * to /cart.js, so one round trip both applies the code and returns the real
 * cart, including `discount_codes[].applicable`. From that answer the field
 * says one of three honest things — applied and here is the saving, accepted
 * but it only lands at checkout, or rejected — and the section re-renders in
 * place. A full page navigation is kept only as the failure fallback.
 *
 * The shipping estimator asks Shopify's AJAX endpoints
 * (/cart/prepare_shipping_rates.json, then /cart/async_shipping_rates.json)
 * and prints the rates. The section is server-rendered with an empty result
 * area, so a re-render after a quantity change clears stale rates.
 */
(function () {
  const root = document.querySelector("[data-gmx-cart]");
  if (!root) return;

  const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const sectionId = root.dataset.sectionId;
  let busy = false;
  let promoBusy = false;
  /* Survives the section re-render, which throws away the DOM message node. */
  let promoState = null;

  /* Whole amounts print as "$18", not "$18.00" — same rule as the Liquid side,
     which uses money_without_trailing_zeros. */
  function money(cents) {
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return "$" + (whole ? String(cents / 100) : (cents / 100).toFixed(2));
    }
  }

  function setBadge(count) {
    document.querySelectorAll("[data-gmx-cart-count]").forEach((node) => {
      node.textContent = count;
      node.dataset.count = count;
    });
  }

  function rowFor(key) {
    return root.querySelector('[data-gmx-row][data-key="' + key + '"]');
  }

  function lock(row) {
    if (!row) return;
    row.classList.add("gmx-cart-row--busy");
    row.querySelectorAll("button, input").forEach((el) => {
      el.disabled = true;
    });
  }

  function render(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const fresh = doc.querySelector("[data-gmx-cart]");
    if (!fresh) {
      window.location.reload();
      return;
    }
    const ship = readShipState();
    root.innerHTML = fresh.innerHTML;
    paintPromo();
    initShipping(ship);
    /* Announced after the swap, not before: the new markup carries fresh
       product cards, and assets/gmx-discounts.js repaints their prices. */
    document.dispatchEvent(new CustomEvent("gmx:cart-changed"));
  }

  /* Section rendering answers with an empty string often enough to plan for it
     (measured on the dev server: roughly one call in three). The cart page
     itself always renders, and render() keeps only the part inside
     [data-gmx-cart], so re-fetching the page is a reliable second try. A
     reload stays as the last resort. */
  function renderFromCartPage() {
    return fetch(routes + "cart", { headers: { Accept: "text/html" } })
      .then((response) => response.text())
      .then((markup) => {
        if (markup.indexOf("data-gmx-cart") !== -1) {
          render(markup);
        } else {
          window.location.reload();
        }
      })
      .catch(() => {
        window.location.reload();
      });
  }

  function showError(row, message) {
    if (!row) {
      window.location.reload();
      return;
    }
    row.classList.remove("gmx-cart-row--busy");
    row.querySelectorAll("button, input").forEach((el) => {
      el.disabled = false;
    });
    let note = row.querySelector("[data-gmx-row-error]");
    if (!note) {
      note = document.createElement("div");
      note.className = "gmx-cart-row__error";
      note.setAttribute("data-gmx-row-error", "");
      row.querySelector(".gmx-cart-row__info").appendChild(note);
    }
    note.textContent = message;
  }

  function change(key, quantity) {
    if (busy) return;
    busy = true;
    const row = rowFor(key);
    lock(row);

    fetch(routes + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: sectionId,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
      .then((result) => {
        busy = false;
        if (!result.ok) {
          showError(row, result.data.description || result.data.message || "That quantity is unavailable.");
          return;
        }
        setBadge(result.data.item_count);
        const html = result.data.sections && result.data.sections[sectionId];
        if (html) {
          render(html);
        } else {
          renderFromCartPage();
        }
      })
      .catch(() => {
        busy = false;
        showError(row, "Something went wrong. Please try again.");
      });
  }

  root.addEventListener("click", (event) => {
    const stepper = event.target.closest("[data-gmx-qty]");
    if (stepper) {
      const key = stepper.dataset.key;
      const input = root.querySelector('[data-gmx-qty-input][data-key="' + key + '"]');
      /* The attribute is data-gmx-qty, so the dataset key is gmxQty. Reading
         `dataset.qty` gave undefined -> NaN, and the stepper never moved. */
      const current = parseInt(input.value, 10);
      const delta = parseInt(stepper.dataset.gmxQty, 10);
      /* Shopify removes a line when its quantity becomes zero. The number field
         still has min="1", so only the minus button can request removal. */
      const next = delta < 0 && current <= 1 ? 0 : Math.max(1, current + delta);
      if (next === parseInt(input.value, 10)) return;
      input.value = next;
      change(key, next);
      return;
    }

    const remove = event.target.closest("[data-gmx-remove]");
    if (remove) {
      change(remove.dataset.key, 0);
      return;
    }

    const apply = event.target.closest("[data-gmx-promo-apply]");
    if (apply) {
      applyPromo();
    }
  });

  root.addEventListener("change", (event) => {
    const input = event.target.closest("[data-gmx-qty-input]");
    if (!input) return;
    const next = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = next;
    change(input.dataset.key, next);
  });

  root.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    if (event.target.closest("[data-gmx-promo-input]")) {
      event.preventDefault();
      applyPromo();
      return;
    }
    if (event.target.closest("[data-gmx-qty-input]")) {
      event.preventDefault();
      event.target.blur();
    }
  });

  /* ------------------------------------------------------------ promo code */

  function promoNodes() {
    return {
      wrap: root.querySelector("[data-gmx-promo]"),
      input: root.querySelector("[data-gmx-promo-input]"),
      btn: root.querySelector("[data-gmx-promo-apply]"),
      label: root.querySelector("[data-gmx-promo-label]"),
      msg: root.querySelector("[data-gmx-promo-msg]"),
    };
  }

  /* Writes promoState onto whatever promo markup is currently in the DOM. It
     runs again after every re-render, so the answer outlives the swap. */
  function paintPromo() {
    const nodes = promoNodes();
    if (!nodes.wrap) return;

    const error = !!promoState && promoState.kind === "error";
    nodes.wrap.classList.toggle("gmx-promo--error", error);

    if (!nodes.msg) return;
    if (!promoState) {
      nodes.msg.hidden = true;
      nodes.msg.textContent = "";
      return;
    }
    nodes.msg.className =
      "gmx-promo__msg" +
      (promoState.kind === "error" ? " gmx-promo__msg--error" : "") +
      (promoState.kind === "note" ? " gmx-promo__msg--note" : "");
    nodes.msg.textContent = promoState.text;
    nodes.msg.hidden = false;

    /* A rejected code stays in the field so it can be corrected, not retyped. */
    if (error && promoState.code && nodes.input && !nodes.input.value) {
      nodes.input.value = promoState.code;
    }
  }

  function setPromoBusy(on) {
    const nodes = promoNodes();
    if (!nodes.btn) return;
    nodes.btn.setAttribute("aria-busy", on ? "true" : "false");
    nodes.btn.disabled = on;
    if (nodes.label) nodes.label.textContent = on ? "Applying" : "Apply";
  }

  /* Last resort only: the old full-page route through Shopify's own handler. */
  function navigateToDiscount(code) {
    window.location.href =
      routes + "discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(routes + "cart");
  }

  function refreshSection() {
    return fetch(routes + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: {}, sections: sectionId, sections_url: window.location.pathname }),
    })
      .then((response) => response.json())
      .then((data) => {
        setBadge(data.item_count);
        const html = data.sections && data.sections[sectionId];
        if (html) {
          render(html);
          return null;
        }
        return renderFromCartPage();
      });
  }

  function applyPromo() {
    const nodes = promoNodes();
    if (!nodes.input || promoBusy) return;

    const code = nodes.input.value.trim();
    if (!code) {
      promoState = { kind: "error", text: "Enter a discount code first." };
      paintPromo();
      nodes.input.focus();
      return;
    }

    promoBusy = true;
    promoState = null;
    paintPromo();
    setPromoBusy(true);

    let before = null;

    fetch(routes + "cart.js", { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => {
        before = cart.total_price;
      })
      .catch(() => {
        /* Without a "before" total the saving is simply not claimed. */
      })
      .then(() =>
        /* Shopify applies the code on this request and, because of the
           redirect parameter, answers with the cart as JSON. */
        fetch(routes + "discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(routes + "cart.js"), {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        }),
      )
      .then((response) => response.json())
      .then((cart) => {
        const entry = (cart.discount_codes || []).filter(
          (item) => String(item.code).toLowerCase() === code.toLowerCase(),
        )[0];
        if (!entry) throw new Error("no-answer");

        if (!entry.applicable) {
          promoBusy = false;
          setPromoBusy(false);
          promoState = { kind: "error", code: code, text: "“" + entry.code + "” is not a valid code for this cart." };
          paintPromo();
          nodes.input.select();
          return null;
        }

        const saved = before === null ? null : before - cart.total_price;
        promoState =
          saved > 0
            ? { kind: "success", code: entry.code, text: entry.code + " applied — you saved " + money(saved) + "." }
            : {
                kind: "note",
                code: entry.code,
                text: entry.code + " accepted. This one applies at checkout, so the total here does not change.",
              };
        return refreshSection().then(() => {
          promoBusy = false;
          setPromoBusy(false);
        });
      })
      .catch(() => {
        /* Anything unexpected (offline, a Shopify error page) falls back to the
           real navigation rather than pretending nothing happened. */
        navigateToDiscount(code);
      });
  }

  /* ------------------------------------------------------ shipping estimate */

  const SHIP_LABELS = { country: "Country", province: "State / province", zip: "ZIP / postal code" };

  /* Bumped by every estimate and every country change; a response whose
     number is no longer current belongs to an old address and is dropped. */
  let shipSeq = 0;

  function shipNodes() {
    return {
      wrap: root.querySelector("[data-gmx-ship]"),
      form: root.querySelector("[data-gmx-ship-form]"),
      country: root.querySelector("[data-gmx-ship-country]"),
      provinceWrap: root.querySelector("[data-gmx-ship-province-wrap]"),
      province: root.querySelector("[data-gmx-ship-province]"),
      zip: root.querySelector("[data-gmx-ship-zip]"),
      btn: root.querySelector("[data-gmx-ship-submit]"),
      label: root.querySelector("[data-gmx-ship-label]"),
      result: root.querySelector("[data-gmx-ship-result]"),
    };
  }

  /* Fills the state/province list for the chosen country (from the
     data-provinces JSON on each option) and hides it when there is none. */
  function syncProvinces() {
    const n = shipNodes();
    if (!n.country || !n.province) return;
    const opt = n.country.options[n.country.selectedIndex];
    let list = [];
    try {
      list = JSON.parse((opt && opt.getAttribute("data-provinces")) || "[]");
    } catch (e) {
      list = [];
    }
    n.province.innerHTML = "";
    list.forEach((pair) => {
      const o = document.createElement("option");
      o.value = pair[0];
      o.textContent = pair[1];
      n.province.appendChild(o);
    });
    n.provinceWrap.hidden = list.length === 0;
  }

  /* What the shopper typed, carried across a section re-render so a quantity
     change does not wipe the address. Rates are not carried: they are stale. */
  function readShipState() {
    const n = shipNodes();
    if (!n.form) return null;
    /* Only a form the shopper opened with the toggle stays open; a cart that
       crosses the free-shipping line closes the default-open form, by design. */
    const opened = !!root.querySelector("[data-gmx-ship-toggle]") && !n.form.hidden;
    return { open: opened, country: n.country.value, province: n.province.value, zip: n.zip.value };
  }

  function initShipping(state) {
    const n = shipNodes();
    if (!n.country) return;
    if (state) {
      n.country.value = state.country;
      n.zip.value = state.zip;
    }
    syncProvinces();
    if (!state) return;
    if (state.province) n.province.value = state.province;
    const toggle = root.querySelector("[data-gmx-ship-toggle]");
    if (toggle && state.open) {
      toggle.setAttribute("aria-expanded", "true");
      n.form.hidden = false;
    }
  }

  function shipMessage(el, className, text) {
    el.innerHTML = "";
    const p = document.createElement("p");
    p.className = className;
    p.textContent = text;
    el.appendChild(p);
  }

  function shipErrorText(status, body) {
    if (body && typeof body === "object") {
      const parts = [];
      Object.keys(body).forEach((field) => {
        const msgs = [].concat(body[field]);
        msgs.forEach((m) => {
          /* Shopify's messages are sometimes whole sentences, sometimes
             fragments ("is invalid"); only fragments need the field name. */
          const text = String(m).replace(/\.$/, "");
          parts.push(/^[A-Z]/.test(text) ? text + "." : (SHIP_LABELS[field] || field) + " " + text + ".");
        });
      });
      if (parts.length) return parts.join(" ");
    }
    return status === 429
      ? "Too many requests. Please wait a moment and try again."
      : "We could not estimate shipping right now. Please try again.";
  }

  function shipQuery(n) {
    const params = new URLSearchParams();
    params.set("shipping_address[country]", n.country.value);
    if (!n.provinceWrap.hidden && n.province.value) params.set("shipping_address[province]", n.province.value);
    params.set("shipping_address[zip]", n.zip.value.trim());
    return params.toString();
  }

  function pollRates(query, tries) {
    return fetch(routes + "cart/async_shipping_rates.json?" + query, { headers: { Accept: "application/json" } }).then(
      (response) =>
        response
          .json()
          .catch(() => null)
          .then((body) => {
            if (!response.ok) throw { status: response.status, body: body };
            if (body === null) {
              if (tries <= 1) throw { status: 0, body: null };
              return new Promise((resolve) => setTimeout(resolve, 500)).then(() => pollRates(query, tries - 1));
            }
            return body;
          }),
    );
  }

  function setShipBusy(n, on) {
    n.btn.disabled = on;
    n.btn.setAttribute("aria-busy", on ? "true" : "false");
    n.label.textContent = on ? "Estimating" : "Estimate";
  }

  function estimateShipping() {
    const n = shipNodes();
    if (!n.form || n.btn.disabled) return;
    if (!n.zip.value.trim()) {
      shipMessage(n.result, "gmx-ship__error", "Enter a ZIP or postal code first.");
      n.zip.focus();
      return;
    }

    const form = n.form;
    const query = shipQuery(n);
    const seq = ++shipSeq;
    n.result.innerHTML = "";
    setShipBusy(n, true);

    fetch(routes + "cart/prepare_shipping_rates.json?" + query, {
      method: "POST",
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (response.ok) return null;
        return response
          .json()
          .catch(() => null)
          .then((body) => {
            throw { status: response.status, body: body };
          });
      })
      .then(() => pollRates(query, 10))
      .then((data) => {
        /* A quantity change re-rendered the section while we waited: the old
           form is gone, and so are its (now stale) rates. */
        if (!form.isConnected || seq !== shipSeq) return;
        const fresh = shipNodes();
        setShipBusy(fresh, false);
        const rates = (data && data.shipping_rates) || [];
        if (!rates.length) {
          shipMessage(fresh.result, "gmx-ship__note", "We don\u2019t ship to this address yet.");
          return;
        }
        const list = document.createElement("ul");
        list.className = "gmx-ship__rates";
        rates.forEach((rate) => {
          const li = document.createElement("li");
          li.className = "gmx-ship__rate";
          const name = document.createElement("span");
          name.textContent = rate.presentment_name || rate.name;
          const price = document.createElement("span");
          const cents = Math.round(parseFloat(rate.price) * 100);
          price.textContent = cents === 0 ? "Free" : money(cents);
          li.appendChild(name);
          li.appendChild(price);
          list.appendChild(li);
        });
        const note = document.createElement("p");
        note.className = "gmx-ship__note";
        note.textContent = "Estimate only. Final shipping is set at checkout.";
        fresh.result.innerHTML = "";
        fresh.result.appendChild(list);
        fresh.result.appendChild(note);
      })
      .catch((err) => {
        if (!form.isConnected || seq !== shipSeq) return;
        const fresh = shipNodes();
        setShipBusy(fresh, false);
        shipMessage(
          fresh.result,
          "gmx-ship__error",
          err && err.status === 0
            ? "Rates are taking too long. Please try again."
            : shipErrorText(err && err.status, err && err.body),
        );
      });
  }

  root.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-gmx-ship-toggle]");
    if (toggle) {
      const n = shipNodes();
      const open = toggle.getAttribute("aria-expanded") !== "true";
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      n.form.hidden = !open;
      if (open) n.country.focus();
      return;
    }
    if (event.target.closest("[data-gmx-ship-submit]")) estimateShipping();
  });

  root.addEventListener("change", (event) => {
    if (event.target.closest("[data-gmx-ship-country]")) {
      syncProvinces();
      const n = shipNodes();
      shipSeq++;
      setShipBusy(n, false);
      n.result.innerHTML = "";
    }
  });

  root.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && event.target.closest("[data-gmx-ship-zip]")) {
      event.preventDefault();
      estimateShipping();
    }
  });

  initShipping();
})();
